"use client";

import { useEffect, useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { RotateCw } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { cn } from "@/lib/utils";
import {
  useIsAdmin,
  useVybeAuthStore,
  presenceConnection,
  showAdminNotification,
} from "@/lib/vybe-auth";
import { usePlatform } from "@/platform/PlatformProvider";
import { useAiCredits } from "@/lib/agent/ai-credits";
import { VybeSignInPanel } from "@/components/agent/VybeSignInPanel";

interface VybeAccountScreenProps {
  onBack: () => void;
}

/** "4h 12m" style session age from a start timestamp (same as HeaderUserChip). */
function sessionAge(startedAt: number | null): string {
  if (!startedAt) return "—";
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function initials(name?: string | null, email?: string): string {
  const source = (name && name.trim()) || email || "";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Mobile Settings sub-screen (proposal 55): the phone-native home for the Vybe Trader
 * identity + presence data the desktop HeaderUserChip popover holds. Same store
 * (`useVybeAuthStore`), same presence singleton — list-form presentation.
 */
export default function VybeAccountScreen({ onBack }: VybeAccountScreenProps) {
  const token = useVybeAuthStore((s) => s.token);
  const user = useVybeAuthStore((s) => s.user);
  const presenceConnected = useVybeAuthStore((s) => s.presenceConnected);
  const sessionStartedAt = useVybeAuthStore((s) => s.sessionStartedAt);
  const setPresenceConnected = useVybeAuthStore((s) => s.setPresenceConnected);
  const applyPresenceSelf = useVybeAuthStore((s) => s.applyPresenceSelf);
  const logout = useVybeAuthStore((s) => s.logout);
  const isAdmin = useIsAdmin();
  const { platform } = usePlatform();
  const { credits, refresh: refreshCredits } = useAiCredits();

  const signedIn = !!token;
  const [reconnecting, setReconnecting] = useState(false);
  const [, forceTick] = useState(0);

  // Keep the session-age row fresh while the screen is open.
  useEffect(() => {
    if (!signedIn) return;
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [signedIn]);

  // Refresh the plan/credits snapshot on open (same idiom as the header popover).
  useEffect(() => {
    if (signedIn) void refreshCredits();
  }, [signedIn, refreshCredits]);

  const reconnect = async () => {
    if (!token || reconnecting) return;
    setReconnecting(true);
    try {
      // Same callbacks VybePresenceConnector wires — a manual cycle of the singleton.
      await presenceConnection.disconnect();
      await presenceConnection.connect(token, platform, {
        onConnectedChange: setPresenceConnected,
        onSelf: applyPresenceSelf,
        onAdminNotification: showAdminNotification,
      });
    } finally {
      setReconnecting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-8 w-8 shrink-0"
        >
          <FaChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <span className="text-sm font-semibold">Vybe Trader</span>
        <span
          className={cn(
            "ml-auto font-mono text-[10px]",
            signedIn && presenceConnected ? "text-green-500" : "text-orange-500"
          )}
        >
          ● {signedIn ? (presenceConnected ? "connected" : "offline") : "signed out"}
        </span>
      </div>

      {!signedIn ? (
        // Signed-out: route into the exact sign-in flow the header chip uses.
        <div className="min-h-0 flex-1">
          <VybeSignInPanel variant="mobile" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Identity card */}
          <div className="px-3 pt-3">
            <div className="rounded-[10px] border border-border overflow-hidden">
              <div className="flex items-center gap-2.5 px-3 py-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-bid-blue to-orange-500 text-[12px] font-bold text-black">
                  {initials(user?.displayName, user?.email)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[12.5px] font-semibold text-foreground">
                      {user?.displayName || user?.email}
                    </span>
                    {isAdmin ? (
                      <span className="inline-flex items-center rounded-lg border border-red-500/50 bg-red-500/10 px-1.5 py-0.5 font-mono text-[8.5px] font-bold uppercase tracking-[0.5px] text-red-500">
                        Admin
                      </span>
                    ) : null}
                  </div>
                  <div className="truncate text-[10.5px] text-muted-foreground mt-px">
                    {user?.email}
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center rounded-[9px] border border-bid-blue bg-bid-blue/10 px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.5px] text-bid-blue">
                  {(credits?.plan ?? user?.plan ?? "free").toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Presence status rows */}
          <div className="px-3 pt-3">
            <div className="rounded-[10px] border border-border overflow-hidden">
              <div className="flex items-center h-6 px-3 bg-muted/30 border-b border-border/60 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
                PRESENCE
              </div>
              <div className="flex items-center justify-between h-9 px-3 border-b border-border/40 text-[12.5px]">
                <span className="text-muted-foreground">Presence</span>
                <span
                  className={cn(
                    "font-mono text-[11px] font-medium",
                    presenceConnected ? "text-green-500" : "text-orange-500"
                  )}
                >
                  ● {presenceConnected ? "connected" : "offline"}
                </span>
              </div>
              <div className="flex items-center justify-between h-9 px-3 text-[12.5px]">
                <span className="text-muted-foreground">Session</span>
                <span className="font-mono text-[11px] text-foreground">
                  {sessionAge(sessionStartedAt)}
                </span>
              </div>
              {!presenceConnected ? (
                <div className="px-3 pb-2 text-[10px] leading-tight text-orange-500">
                  You&apos;re signed in but the presence connection is down —
                  retrying automatically.
                </div>
              ) : null}
            </div>
          </div>

          {/* Actions */}
          <div className="px-3 pt-3 pb-4 space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-full text-xs"
              disabled={reconnecting}
              onClick={() => void reconnect()}
            >
              <RotateCw
                className={cn("h-3.5 w-3.5", reconnecting && "animate-spin")}
              />
              {reconnecting ? "Reconnecting…" : "Reconnect"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-full text-xs text-destructive hover:text-destructive"
              onClick={() => {
                void logout();
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
